import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { createCheckoutSession, syncCheckout, getMe } from '../utils/api';

// access.expiresAt is a millisecond epoch timestamp.
const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const SubscribePage = () => {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const syncStarted = useRef(false);

  const access = user?.access;
  const active = access?.active;

  // Handle the return from Stripe Checkout: ?status=success&session_id=...
  useEffect(() => {
    if (syncStarted.current) return;
    const returnStatus = searchParams.get('status');
    const sessionId = searchParams.get('session_id');
    if (returnStatus !== 'success' || !sessionId) return;

    syncStarted.current = true;
    setSyncing(true);

    let cancelled = false;
    const run = async () => {
      try {
        await syncCheckout(sessionId);
      } catch (err) {
        // Non-fatal — the webhook may still catch up; fall through to polling.
      }
      // Clear the query params so a refresh doesn't re-trigger.
      setSearchParams({}, { replace: true });

      await refresh();

      // Poll getMe every 2s up to 20s waiting for access to activate.
      const deadline = Date.now() + 20000;
      /* eslint-disable no-await-in-loop */
      while (!cancelled && Date.now() < deadline) {
        try {
          const me = await getMe();
          if (me?.access?.active) {
            if (!cancelled) navigate('/', { replace: true });
            return;
          }
        } catch (err) {
          // ignore transient errors while polling
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      /* eslint-enable no-await-in-loop */

      if (!cancelled) {
        await refresh();
        setSyncing(false);
      }
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, refresh, navigate]);

  const handleBuy = useCallback(async () => {
    setError(null);
    setNotConfigured(false);
    setBusy(true);
    try {
      const { url } = await createCheckoutSession();
      window.location.href = url;
    } catch (err) {
      if (err?.response?.data?.error === 'stripe_not_configured') {
        setNotConfigured(true);
      } else {
        setError('Could not start checkout. Please try again.');
      }
      setBusy(false);
    }
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const renderStatus = () => {
    if (syncing) {
      return (
        <div className="flex flex-col items-center gap-3 py-4">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
          <p className="text-gray-300">Confirming your payment…</p>
        </div>
      );
    }

    if (active) {
      const until = formatDate(access?.expiresAt);
      return (
        <div className="rounded bg-green-900 bg-opacity-30 text-green-300 px-4 py-3">
          <p className="font-semibold">Your access is active.</p>
          {until && <p className="text-sm mt-1">Access until {until}.</p>}
        </div>
      );
    }

    return (
      <div className="rounded bg-surface border border-gray-700 px-4 py-3 text-gray-300">
        <p>You don&apos;t have active access yet.</p>
      </div>
    );
  };

  return (
    <div className="flex justify-center items-center min-h-[60vh]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-surface rounded-lg p-8 w-full max-w-md shadow-lg"
      >
        <h1 className="text-2xl font-bold text-center mb-2">Arcade Access</h1>
        {user?.email && (
          <p className="text-center text-gray-400 mb-6 text-sm">
            Signed in as {user.email}
          </p>
        )}

        <div className="mb-6">{renderStatus()}</div>

        {notConfigured && (
          <div className="text-sm text-yellow-300 bg-yellow-900 bg-opacity-30 rounded px-3 py-2 mb-4">
            Payments aren&apos;t set up on this server yet. Please check back soon.
          </div>
        )}

        {error && (
          <div className="text-sm text-red-400 bg-red-900 bg-opacity-30 rounded px-3 py-2 mb-4">
            {error}
          </div>
        )}

        {!syncing && (
          <div className="space-y-3">
            {active ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="btn btn-primary w-full"
                >
                  Go to games
                </button>
                <button
                  type="button"
                  onClick={handleBuy}
                  disabled={busy}
                  className="btn btn-secondary w-full disabled:opacity-60"
                >
                  {busy ? 'Please wait…' : 'Extend another month — A$5'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleBuy}
                  disabled={busy}
                  className="btn btn-primary w-full disabled:opacity-60"
                >
                  {busy ? 'Please wait…' : 'Buy 1 month — A$5'}
                </button>
                <p className="text-xs text-center text-gray-500">
                  One-time payment for 30 days of access. Does not auto-renew.
                </p>
              </>
            )}
          </div>
        )}

        <div className="text-center mt-6">
          <button
            type="button"
            onClick={handleLogout}
            className="text-sm text-gray-400 hover:text-secondary"
          >
            Log out
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default SubscribePage;
